import { ErrorListResponse, instanceOfErrorListResponse } from '@genial/services';
import { OfferRequest } from '@genial/services/dist/models';

import { ProductButtonProps } from '../..';
import { useToastCreator } from '../../../../../../hooks/useToastCreator';
import { AssetSelectedTypeMap } from '../../../../../../store/product/types';
import { errorMessageBatchTicketRequestItem } from '../../../../../BatchTicketRequests/utils';
import { useBatchTicketAssetsContext } from '../../../../contexts';
import {
  deleteProductBasketService,
  postProductBasketService,
} from '../../../../services/products';
import { AssetButton } from '../../components/AssetButton';

import { OnlyBatchTicketProducts, propertiesMap } from './constants';

export interface BatchTicketButtonProps<T extends keyof AssetSelectedTypeMap>
  extends ProductButtonProps<T> {
  tooltip?: string;
  idSelector: keyof AssetSelectedTypeMap[T];
}

export const BatchTicketButton = <T extends keyof AssetSelectedTypeMap>({
  id,
  offer,
  tooltip,
  isDisabled,
  idSelector,
  productContextConstant,
}: BatchTicketButtonProps<T>) => {
  const { showToast, showToastFromError } = useToastCreator(`toast-${id}-offer-basket`);

  const {
    deselectProduct,
    selectProduct,
    selectedProducts,
    setLoadingProduct,
    resetLoadingProduct,
    basketAdvisorOrderId,
  } = useBatchTicketAssetsContext();

  const isSelected = selectedProducts[id].items.some(
    (product) => product[idSelector] === offer[idSelector]
  );

  const handlePress = async () => {
    try {
      setLoadingProduct(id, offer);
      if (isSelected) {
        const productCartItem = selectedProducts[id].items.find(
          (product) => product[idSelector] === offer[idSelector]
        );

        await deleteProductBasketService({
          basketAdvisorOfferId: (productCartItem as any)?.basketOfferId!,
        });
        deselectProduct(id, offer, idSelector);
      } else {
        const propertiesMapper = propertiesMap[id as OnlyBatchTicketProducts];
        const response = await postProductBasketService({
          productId: offer[idSelector] as string,
          basketAdvisorOrderId: basketAdvisorOrderId!,
          offer: offer as unknown as OfferRequest,
          ...propertiesMapper(offer as any),
        });

        const selectedProductData = {
          ...offer,
          basketOfferId: response.basketOffer.basketAdvisorOfferId,
        };

        selectProduct(id, selectedProductData);
      }
    } catch (error) {
      if (instanceOfErrorListResponse(error as object)) {
        (error as ErrorListResponse).errors?.forEach((errorItem) => showToastFromError(errorItem));
        return;
      }

      showToast(...errorMessageBatchTicketRequestItem);
    } finally {
      resetLoadingProduct(id, offer, idSelector);
    }
  };

  return (
    <AssetButton
      id={id}
      offer={offer}
      tooltip={tooltip}
      isDisabled={isDisabled}
      onPress={handlePress}
      isSelected={isSelected}
      productContextConstant={productContextConstant}
    />
  );
};
